<template>
  <div class="tc-project-picker" ref="pickerRef">
    <button
      ref="triggerRef"
      type="button"
      class="tc-project-picker-btn"
      :class="{ 'has-value': !!modelValue }"
      data-testid="employee-picker-trigger"
      @click="toggleOpen"
    >
      <span v-if="modelValue" class="tc-project-picker-value">
        <span class="tc-project-picker-wbs txt-mono">{{ modelValue.employee }}</span>
        <span class="tc-project-picker-name">{{ displayName(modelValue) }}</span>
      </span>
      <span v-else class="tc-project-picker-placeholder">Search employees…</span>
      <span class="tc-project-picker-chevron" aria-hidden="true">▾</span>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        class="tc-project-dropdown tc-employee-dropdown--floating"
        :style="dropdownStyle"
        data-testid="employee-picker-dropdown"
      >
        <input
          ref="searchInput"
          type="search"
          class="tc-project-search-input"
          placeholder="Type at least 2 characters (name, code, or email)…"
          autocomplete="off"
          data-testid="employee-picker-search"
          :value="query"
          @input="onInput"
        />

        <div v-if="isLoading" class="tc-project-loading">Searching…</div>

        <div v-else-if="isError" class="tc-project-error">
          {{ errorMessage }}
        </div>

        <ul
          v-else-if="results.length > 0"
          class="tc-project-results"
          role="listbox"
          data-testid="employee-picker-results"
        >
          <li
            v-for="emp in results"
            :key="`${emp.employee}/${emp.employeeCompany}`"
            role="option"
            class="tc-project-result-item"
            :class="{ selected: modelValue?.employee === emp.employee }"
            data-testid="employee-picker-result"
            @click="select(emp)"
          >
            <span class="tc-project-result-wbs txt-mono">{{ emp.employee }}</span>
            <span class="tc-project-result-name">{{ displayName(emp) }}</span>
            <span v-if="emp.email" class="tc-employee-result-email">{{ emp.email }}</span>
          </li>
        </ul>

        <div v-else-if="query.length >= 2" class="tc-project-empty">No employees found.</div>

        <div v-else class="tc-project-empty">Type at least 2 characters to search.</div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { searchEmployees } from '../api/admin';
import type { Employee } from '../api/admin';
import { ApiClientError } from '../api/client';

defineProps<{
  modelValue: Employee | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [employee: Employee | null];
}>();

const pickerRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLButtonElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const open = ref(false);
const query = ref('');
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const debouncedQuery = ref('');

// Viewport position recomputed each frame the dropdown is open. Uses fixed
// positioning relative to the viewport so the dropdown escapes the modal's
// overflow:auto clip without inheriting modal scroll behavior.
const dropdownRect = ref<{ top: number; left: number; width: number } | null>(null);
const dropdownStyle = computed(() => {
  if (!dropdownRect.value) return { display: 'none' };
  return {
    position: 'fixed' as const,
    top: `${dropdownRect.value.top}px`,
    left: `${dropdownRect.value.left}px`,
    width: `${dropdownRect.value.width}px`,
    zIndex: 1100,
  };
});

function updateDropdownPosition() {
  const el = triggerRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  dropdownRect.value = {
    top: rect.bottom + 4,
    left: rect.left,
    width: rect.width,
  };
}

function onInput(e: Event) {
  const val = (e.target as HTMLInputElement).value;
  query.value = val;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debouncedQuery.value = val;
  }, 250);
}

const searchEnabled = computed(() => debouncedQuery.value.length >= 2);

const { data, isFetching, isError, error } = useQuery({
  queryKey: computed(() => ['admin', 'employees', 'search', debouncedQuery.value]),
  queryFn: () => searchEmployees(debouncedQuery.value),
  enabled: searchEnabled,
  placeholderData: (prev) => prev,
  retry: false,
  staleTime: 30_000,
});

const isLoading = computed(() => isFetching.value && searchEnabled.value);
const results = computed<Employee[]>(() => data.value?.employees ?? []);
const errorMessage = computed(() => {
  const err = error.value;
  if (err instanceof ApiClientError) return err.message;
  return 'Search failed';
});

function toggleOpen() {
  open.value = !open.value;
  if (open.value) {
    nextTick(() => {
      updateDropdownPosition();
      searchInput.value?.focus();
    });
  }
}

function select(emp: Employee) {
  emit('update:modelValue', emp);
  open.value = false;
  query.value = '';
  debouncedQuery.value = '';
}

function displayName(emp: Employee): string {
  const first = emp.preferredName || emp.firstName;
  const parts = [first, emp.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : emp.employee;
}

function onOutsideClick(e: MouseEvent) {
  const target = e.target as Node;
  if (pickerRef.value?.contains(target)) return;
  // The dropdown lives outside pickerRef (teleported to body), so check it
  // by class/closest. Clicks inside the floating dropdown should NOT close.
  if ((target as HTMLElement).closest?.('.tc-employee-dropdown--floating')) return;
  open.value = false;
}

function onScrollOrResize() {
  if (open.value) updateDropdownPosition();
}

watch(open, (isOpen) => {
  if (isOpen) {
    document.addEventListener('mousedown', onOutsideClick);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
  } else {
    document.removeEventListener('mousedown', onOutsideClick);
    window.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);
  }
});

onUnmounted(() => {
  document.removeEventListener('mousedown', onOutsideClick);
  window.removeEventListener('scroll', onScrollOrResize, true);
  window.removeEventListener('resize', onScrollOrResize);
  if (debounceTimer) clearTimeout(debounceTimer);
});
</script>
